import Workspace from 'iam/models/Workspace'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { task, Task } from 'mobx-task'
import { Observable, Subscription, switchMap } from 'rxjs'
import { throttle } from 'lodash'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { shareLatest } from 'utils/rx-util'
import OCRScopeWork from 'wunder/OCRScopeWork'
import OCRScopeWorkStore from 'wunder/OCRScopeWorkStore'
import { AggregationResponseDto } from 'wunder/dtos/ocr-scope-work'
import OCRScopeWorkWorkspaceSearchViewModel from './OCRScopeWorkWorkspaceSearchViewModel'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { Id } from 'types/definitions'
import Member from 'iam/models/Member'
import OCRScopeWorkQueryStringBuilder, {
  OCRScopingStatusNameFilterOptions,
} from '../../OCRScopeWorkQueryStringBuilder'
import OCRScopingJobsPermissionViewModel from '../../OCRScopingJobsPermissionViewModel'
import OCRScopingJobMainPanelViewModel from '../MainPanel/OCRScopingJobMainPanelViewModel'
import { AlertFactory } from '../../../../../alerts/Alert'
import JobStore from '../../../../JobStore'
import ModalViewModel from 'infra/ModalViewModel'
import OCRScopingJobBulkActionViewModel from './OCRScopingJobBulkActionViewModel'

export type OCRScopeWorkWorkspaceFilter = WorkspaceOCRScopeWork | AllWorkspaces

export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

export interface WorkspaceOCRScopeWork {
  type: 'Workspace'
  workspace: Workspace
}

export enum OCRScopeWorkStatusFilterTypeId {
  ScopingInProgress = OCRScopingStatusNameFilterOptions.SCOPING_IN_PROGRESS,
  ErrorOccurred = OCRScopingStatusNameFilterOptions.ERROR_OCCURRED,
  UnderReview = OCRScopingStatusNameFilterOptions.REVIEW_REQUIRED +
    OCRScopingStatusNameFilterOptions.REVIEW_SUGGESTED,
  ReadyToSubmit = OCRScopingStatusNameFilterOptions.READY_TO_SUBMIT,
}

export type OCRScopeWorkStatusFilterType = {
  id: OCRScopeWorkStatusFilterTypeId
  title: string
  subtitle: string
  headerName: string
  bulkAction?: {
    bulkActionName: string
    bulkActionHandler: Task<[], Promise<void>>
    bulkActionModal?: ModalViewModel
  }
  query: string
  count: () => number
}

export default class OCRScopingJobSidePanelViewModel {
  private subscription = new Subscription()

  workspaceSelectorViewModel = new OCRScopeWorkWorkspaceSearchViewModel(
    this.getCurrentUserWorkspaceMemberships
  )

  searchText = new SearchTextViewModel()

  query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() => this.fetchItems()),
    shareLatest()
  )

  scrollProvider = new InfiniteScrollProvider<OCRScopeWork>({
    limit: 30,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this.ocrScopeWorkStore.search({
        limit: 30,
        aggregations:
          this.selectedStatusFilter?.query ??
          new OCRScopeWorkQueryStringBuilder()
            .withArchivedStatus(false)
            .build(),
        search_text: this.searchText.value,
        workspace_id: this.selectedWorkspaceId,
        after: paging.after ?? '',
      }),
  })

  ocrScopingJobBulkActionViewModel = new OCRScopingJobBulkActionViewModel(
    this.ocrScopingJobsPermissionViewModel,
    this.ocrScopeWorkStore,
    this.jobStore,
    this.scrollProvider,
    this.newAlert
  )

  @observable aggregations: AggregationResponseDto = []

  @observable showSearchField = false

  @observable currentMemberWorkspaceId: Id = Id.none

  @observable isDoneFetchingAggregations = false

  @observable
  selectedStatusFilter?: OCRScopeWorkStatusFilterType = undefined

  @observable
  workspaceFilter: OCRScopeWorkWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  @observable statusFilterOptions: OCRScopeWorkStatusFilterType[] = []

  constructor(
    private ocrScopingJobsPermissionViewModel: OCRScopingJobsPermissionViewModel,
    private ocrScopingJobMainPanelViewModel: OCRScopingJobMainPanelViewModel,
    private ocrScopeWorkStore: OCRScopeWorkStore,
    private jobStore: JobStore,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private currentMember$: Observable<Member>,
    private newAlert: AlertFactory
  ) {
    makeObservable(this)

    this.currentMember$.subscribe((member) => {
      this.currentMemberWorkspaceId = member.workspace.id

      if (
        member.workspace !== Workspace.none &&
        !this.canManageOCRScopingJobs &&
        this.canViewScopingJobs
      ) {
        this.selectWorkspaceForFiltering(member.workspace)
      }
    })

    reaction(
      () => this.workspaceFilter,
      () => {
        this.fetchItems()
        this.loadScopingAggregations()
      }
    )
  }

  @computed
  get canViewScopingJobs() {
    return this.ocrScopingJobsPermissionViewModel.canViewScopingJobs
  }

  @computed
  get canManageOCRScopingJobs() {
    return this.ocrScopingJobsPermissionViewModel.canManageOCRScopingJobs
  }

  @computed
  get workspaces() {
    return this.workspaceSelectorViewModel.filteredWorkspaces
  }

  @computed
  get ocrScopeWorkJobs() {
    return this.scrollProvider.items
  }

  @computed
  get aggregationStatuses() {
    const status = this.aggregations.find((a) => a.field === 'status_name')

    return status?.buckets
  }

  @computed
  get readyToSubmitCount() {
    return (
      this.aggregationStatuses?.find(
        (i) => i.value === OCRScopingStatusNameFilterOptions.READY_TO_SUBMIT
      )?.count ?? 0
    )
  }

  @computed
  get reviewSuggestedCount() {
    return (
      this.aggregationStatuses?.find(
        (i) => i.value === OCRScopingStatusNameFilterOptions.REVIEW_SUGGESTED
      )?.count ?? 0
    )
  }

  @computed
  get reviewRequiredCount() {
    return (
      this.aggregationStatuses?.find(
        (i) => i.value === OCRScopingStatusNameFilterOptions.REVIEW_REQUIRED
      )?.count ?? 0
    )
  }

  @computed
  get underReviewCount() {
    return this.reviewSuggestedCount + this.reviewRequiredCount
  }

  @computed
  get errorOccurredCount() {
    return (
      this.aggregationStatuses?.find(
        (i) => i.value === OCRScopingStatusNameFilterOptions.ERROR_OCCURRED
      )?.count ?? 0
    )
  }

  @computed
  get scopeInProgressCount() {
    return (
      this.aggregationStatuses?.find(
        (i) => i.value === OCRScopingStatusNameFilterOptions.SCOPING_IN_PROGRESS
      )?.count ?? 0
    )
  }

  @computed
  get selectedWorkspaceId() {
    if (
      (this.workspaceFilter.type === 'AllWorkspaces' ||
        this.workspaceFilter.workspace === Workspace.none) &&
      this.canManageOCRScopingJobs
    ) {
      return ''
    }

    if (
      this.workspaceFilter.type === 'Workspace' &&
      this.workspaceFilter.workspace.id
    ) {
      return this.workspaceFilter.workspace.id
    }

    return Id.assert(this.currentMemberWorkspaceId)
  }

  @action.bound
  setStatusFilterOptions() {
    this.statusFilterOptions = [
      {
        id: OCRScopeWorkStatusFilterTypeId.ReadyToSubmit,
        title: 'Ready to submit',
        subtitle: 'Jobs that are for submission',
        headerName: 'Job name',
        bulkAction: {
          bulkActionName: 'Submit',
          bulkActionHandler: this.ocrScopingJobBulkActionViewModel.bulkSubmit,
          bulkActionModal:
            this.ocrScopingJobBulkActionViewModel.confirmBulkSubmitModalVM,
        },
        query: new OCRScopeWorkQueryStringBuilder()
          .withArchivedStatus(false)
          .withStatusNames([OCRScopingStatusNameFilterOptions.READY_TO_SUBMIT])
          .build(),
        count: () => this.readyToSubmitCount,
      },
      {
        id: OCRScopeWorkStatusFilterTypeId.UnderReview,
        title: this.canManageOCRScopingJobs
          ? 'Ready for review'
          : 'Review required',
        subtitle: 'Manual review required',
        headerName: 'Job name',
        bulkAction: this.canManageOCRScopingJobs
          ? {
              bulkActionName: 'Set As Ready To Submit',
              bulkActionHandler:
                this.ocrScopingJobBulkActionViewModel.bulkReview,
              bulkActionModal:
                this.ocrScopingJobBulkActionViewModel.confirmBulkReviewModalVM,
            }
          : undefined,
        query: new OCRScopeWorkQueryStringBuilder()
          .withArchivedStatus(false)
          .withStatusNames([
            OCRScopingStatusNameFilterOptions.REVIEW_REQUIRED,
            OCRScopingStatusNameFilterOptions.REVIEW_SUGGESTED,
          ])
          .build(),
        count: () => this.underReviewCount,
      },
      {
        id: OCRScopeWorkStatusFilterTypeId.ErrorOccurred,
        title: 'Errors',
        subtitle: 'View scoping errors',
        headerName: 'File name',
        bulkAction: this.canManageOCRScopingJobs
          ? {
              bulkActionName: 'Rerun OCR',
              bulkActionHandler:
                this.ocrScopingJobBulkActionViewModel.bulkRerun,
              bulkActionModal:
                this.ocrScopingJobBulkActionViewModel.confirmBulkRerunModalVM,
            }
          : undefined,
        query: new OCRScopeWorkQueryStringBuilder()
          .withArchivedStatus(false)
          .withStatusNames([OCRScopingStatusNameFilterOptions.ERROR_OCCURRED])
          .build(),
        count: () => this.errorOccurredCount,
      },
      {
        id: OCRScopeWorkStatusFilterTypeId.ScopingInProgress,
        title: 'Processing',
        subtitle: 'Scoping in progress',
        headerName: 'File name',
        query: new OCRScopeWorkQueryStringBuilder()
          .withArchivedStatus(false)
          .withStatusNames([
            OCRScopingStatusNameFilterOptions.SCOPING_IN_PROGRESS,
          ])
          .build(),
        count: () => this.scopeInProgressCount,
      },
    ]
  }

  @action.bound
  async setStatusFilter(id?: OCRScopeWorkStatusFilterTypeId) {
    const filter = id
      ? this.statusFilterOptions.find((f) => f.id === id)
      : undefined

    if (this.selectedStatusFilter === filter) {
      return
    }

    this.selectedStatusFilter = filter
    this.ocrScopingJobBulkActionViewModel.activate(this.selectedStatusFilter)
    await Promise.all([this.loadScopingAggregations(), this.fetchItems()])
  }

  @action.bound
  toggleSearchField() {
    this.showSearchField = !this.showSearchField
    if (!this.showSearchField) {
      this.searchText.clear()
    }
  }

  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'Workspace' }
  }

  @action.bound
  setAggregations(aggregations: AggregationResponseDto) {
    this.aggregations = aggregations
  }

  @action.bound
  setIsDoneFetchingAggregations(isDoneFetchingAggregations: boolean) {
    this.isDoneFetchingAggregations = isDoneFetchingAggregations
  }

  @action.bound
  addScrollProviderItems(item: OCRScopeWork) {
    this.scrollProvider.items.unshift(item)
  }

  @action.bound
  removeScrollProviderItem(id: Id) {
    this.ocrScopingJobBulkActionViewModel.itemsSelectedForBulkAction.delete(id)
    this.scrollProvider.items = this.scrollProvider.items.filter(
      (i) => i.id !== id
    )
  }

  activate = task.resolved(async () => {
    this.setStatusFilterOptions()
    this.manageSubscriptions()
    await this.loadScopingAggregations()
  })

  deactivate() {
    this.setIsDoneFetchingAggregations(false)
    this.subscription.unsubscribe()
    this.searchText.clear()
    this.workspaceSelectorViewModel.deactivate()
  }

  private manageSubscriptions() {
    // Search text subscription
    this.subscription.add(this.query$.subscribe())

    const belongsToWorkspaceSelected = (workspaceId: string) => {
      return (
        this.selectedWorkspaceId === '' ||
        this.selectedWorkspaceId === workspaceId
      )
    }

    const isAlreadyInList = (jobScopeId: Id) => {
      return (
        this.scrollProvider.items.find((i) => i.id === jobScopeId) !== undefined
      )
    }

    const removeItemFromList = (jobScopeId: Id) => {
      if (
        this.ocrScopingJobMainPanelViewModel.selectedScopeWork.id !== jobScopeId
      ) {
        this.ocrScopeWorkStore.ocrScopeWorks.remove(Id.assert(jobScopeId))
      }

      if (!isAlreadyInList(jobScopeId)) {
        return
      }
      this.removeScrollProviderItem(jobScopeId)
    }

    const addOrUpdateItemToList = (jobScope: OCRScopeWork) => {
      if (isAlreadyInList(jobScope.id)) {
        return
      }
      this.addScrollProviderItems(jobScope)
    }

    // Scope work created subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobCreatedEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        if (!belongsToWorkspaceSelected(e.data.workspace_id)) {
          return
        }
        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)

        if (
          this.selectedStatusFilter?.id !==
            OCRScopeWorkStatusFilterTypeId.ScopingInProgress ||
          this.searchText.value !== '' ||
          isAlreadyInList(item.id)
        ) {
          return
        }

        addOrUpdateItemToList(item)
      })
    )

    // Scope work updated subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobUpdatedEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        if (!belongsToWorkspaceSelected(e.data.workspace_id)) {
          return
        }
        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)

        if (!this.selectedStatusFilter?.query.includes(e.data.status_name)) {
          removeItemFromList(item.id)
          return
        }

        addOrUpdateItemToList(item)
      })
    )

    // Scope work needs attention subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobNeedsAttentionEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        if (!belongsToWorkspaceSelected(e.data.workspace_id)) {
          return
        }
        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)

        if (
          this.selectedStatusFilter?.id !==
          OCRScopeWorkStatusFilterTypeId.UnderReview
        ) {
          removeItemFromList(item.id)
          return
        }

        addOrUpdateItemToList(item)
      })
    )

    // Scope work errored subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobErroredEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        if (!belongsToWorkspaceSelected(e.data.workspace_id)) {
          return
        }
        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)

        if (
          this.selectedStatusFilter?.id !==
          OCRScopeWorkStatusFilterTypeId.ErrorOccurred
        ) {
          removeItemFromList(e.data.id)
          return
        }

        addOrUpdateItemToList(item)
      })
    )

    // Scope work submitted subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobSubmittedEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)
        removeItemFromList(item.id)
      })
    )

    // Scope work archived subscription
    this.subscription.add(
      this.ocrScopeWorkStore.scopeJobArchivedEvents$?.subscribe((e) => {
        this.loadScopingAggregations()

        const item = this.ocrScopeWorkStore.ocrScopeWorks.set(e.data)
        removeItemFromList(item.id)
      })
    )
  }

  fetchItems = task.resolved(async () => {
    if (!this.selectedStatusFilter) {
      return
    }
    await this.scrollProvider.fetch()
  })

  _loadScopingAggregations = throttle(
    async () => {
      const aggregations = await this.ocrScopeWorkStore.aggregations(
        this.selectedWorkspaceId
      )
      this.setAggregations(aggregations)
      this.setIsDoneFetchingAggregations(true)
    },
    600,
    { leading: true, trailing: true }
  )

  loadScopingAggregations = task.resolved(this._loadScopingAggregations)
}
